import { Box, Typography, Slider } from "@mui/joy";

type ChangeEvent<T> = {
  minValue: T | undefined;
  maxValue: T | undefined;
};

type Props<T extends string | number | null> = {
  choices: {
    value: T;
    label: string;
  }[];
  minValue: T | undefined;
  maxValue: T | undefined;
  onChange: (change: ChangeEvent<T>) => void;
  reverse?: boolean;
  fixedEnd?: "min" | "max";
  valueSuffix?: string;
};

export default function ChoiceRangeSlider<T extends string | number | null>({
  choices,
  minValue,
  maxValue,
  valueSuffix,
  onChange,
  reverse,
  fixedEnd,
}: Props<T>) {
  const values: T[] = choices.map((choice) => choice.value);

  const minIndex = minValue ? values.indexOf(minValue) : 0;
  const maxIndex = maxValue ? values.indexOf(maxValue) : choices.length - 1;

  const value0 = reverse ? choices.length - 1 - maxIndex : minIndex;
  const value1 = reverse ? choices.length - 1 - minIndex : maxIndex;

  let minLabel = minValue === undefined ? choices[0].label : choices[minIndex].label;
  let maxLabel =
    maxValue === undefined ? choices[choices.length - 1].label : choices[maxIndex].label;

  if (reverse) {
    [minLabel, maxLabel] = [maxLabel, minLabel];
  }

  return (
    <Box>
      <Typography
        sx={{
          textAlign: "center",
          fontSize: 16,
          fontWeight: "bold",
        }}
      >
        {minLabel} – {maxLabel} {valueSuffix}
      </Typography>
      <Slider
        value={[value0, value1]}
        onChange={(_, value) => {
          if (!(value instanceof Array)) {
            return;
          }
          const minIndex = reverse ? choices.length - 1 - value[1] : value[0];
          const maxIndex = reverse ? choices.length - 1 - value[0] : value[1];
          let newMinValue = minIndex === 0 ? undefined : values[minIndex];
          let newMaxValue = maxIndex === choices.length - 1 ? undefined : values[maxIndex];

          if (fixedEnd === "min") {
            newMinValue = minValue;
          }
          if (fixedEnd === "max") {
            newMaxValue = maxValue;
          }
          if (newMinValue === minValue && newMaxValue === maxValue) {
            return;
          }

          onChange({
            minValue: newMinValue,
            maxValue: newMaxValue,
          });
        }}
        valueLabelFormat={(index) => values[index as number]}
        min={0}
        max={choices.length - 1}
        step={1}
      />
    </Box>
  );
}
